'use es6';

import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { activeCurrencySelector } from 'pricing-pages-unified-ui/selectors/CurrencySelectors';
import { hasPricingAndPurchasingSucceededSelector } from 'pricing-pages-unified-ui/selectors/LoadingStateSelectors';
import { BATTLESHIP } from 'HubStyleTokens/colors';
import UISubheader from 'UIComponents/layout/UISubheader';
import FormattedMessage from 'I18n/components/FormattedMessage';
import SuiteProductLevelIdentifiersMap from 'pricing-pages-unified-ui/constants/SuiteProductLevelIdentifiersMap';
import { WEB_FONT_DEMI_BOLD_WEIGHT } from 'HubStyleTokens/misc';
import { ProductDescriptions } from './ProductDescriptions';
import { SuiteTileCalculatorAnchorLink } from './SuiteTileCalculatorAnchorLink';
import HR from 'UIComponents/elements/HR';
import SkeletonLoadWrapper from '../../loading/SkeletonLoadWrapper';
import { CrmSuitePrice } from './CrmSuitePrice';
import SuiteTileCTAsContainer from '../SuiteTileCTAsContainer';
import SuiteViewAllFeatures from '../SuiteViewAllFeatures';
import { SUITE_CARD } from '../../../constants/Tracking';
import { ENTERPRISE, PROFESSIONAL, STARTER } from 'self-service-api/constants/ProductLevels';
import { useQueryParamTerm } from '../../../utils/stateUtils';
import SuiteTermSelectorContainer from '../SuiteTermSelectorContainer';
import UIFlex from 'UIComponents/layout/UIFlex';
import { Section } from '../../common/MerchCardSharedComponents';
import CurrentPlanTag from '../../common/CurrentPlanTag';
import { ownsCrmSuiteProductLevel } from '../../../selectors/ProductInventorySelectors';
const StyledHR = styled(HR).withConfig({
  displayName: "SuiteTile__StyledHR",
  componentId: "owa2m4-0"
})(["background-color:", ";"], BATTLESHIP);

const getStringKeysForProductLevel = productLevel => {
  switch (productLevel) {
    case STARTER:
      return {
        titleKey: 'pricingPage.pages.crmSuite.tiers.starter.title',
        subtitleKey: 'pricingPage.pages.crmSuite.tiers.starter.subtitle'
      };

    case PROFESSIONAL:
      return {
        titleKey: 'pricingPage.pages.crmSuite.tiers.professional.title',
        subtitleKey: 'pricingPage.pages.crmSuite.tiers.professional.subtitle'
      };

    case ENTERPRISE:
      return {
        titleKey: 'pricingPage.pages.crmSuite.tiers.enterprise.title',
        subtitleKey: 'pricingPage.pages.crmSuite.tiers.enterprise.subtitle'
      };

    default:
      return null;
  }
};

export const SuiteTile = ({
  productLevel,
  commonStyles
}) => {
  const currencyCode = useSelector(activeCurrencySelector);
  const hasPricingAndPurchasingSucceeded = useSelector(hasPricingAndPurchasingSucceededSelector);
  const isCurrentPlanLevel = useSelector(state => ownsCrmSuiteProductLevel(state, {
    productLevel
  }));
  const {
    setQueryParamTerm,
    queryParamTerm
  } = useQueryParamTerm();
  const {
    titleKey,
    subtitleKey
  } = getStringKeysForProductLevel(productLevel);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs(Section, {
      "data-selenium-id": `${SuiteProductLevelIdentifiersMap[productLevel]}-merch-card`,
      className: "flex flex-column align-center justify-center p-top-6",
      style: Object.assign({}, commonStyles, {
        msGridRow: 1,
        gridRowStart: 1,
        position: 'relative'
      }),
      children: [isCurrentPlanLevel && /*#__PURE__*/_jsx(CurrentPlanTag, {}), /*#__PURE__*/_jsxs(UIFlex, {
        justify: "between",
        align: "end",
        className: "m-bottom-5",
        children: [/*#__PURE__*/_jsx(UISubheader, {
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: titleKey
          }),
          className: "p-bottom-0 m-bottom-0"
        }), /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
          isLoading: !hasPricingAndPurchasingSucceeded,
          heights: [16, 23, 16],
          widths: [60, 70, 130],
          className: "flex-column align-end",
          Content: CrmSuitePrice,
          contentProps: {
            productLevel
          }
        })]
      })]
    }), /*#__PURE__*/_jsx(Section, {
      style: Object.assign({}, commonStyles, {
        msGridRow: 2,
        gridRowStart: 2
      }),
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: subtitleKey
      })
    }), /*#__PURE__*/_jsx(Section, {
      style: Object.assign({}, commonStyles, {
        msGridRow: 3,
        gridRowStart: 3
      }),
      children: !isCurrentPlanLevel && /*#__PURE__*/_jsx("div", {
        className: "m-top-3",
        children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
          isLoading: !hasPricingAndPurchasingSucceeded,
          heights: [41],
          Content: SuiteTermSelectorContainer,
          contentProps: {
            productLevel,
            onChange: term => setQueryParamTerm(term),
            queryParamTerm,
            trackingSource: SUITE_CARD
          }
        })
      })
    }), /*#__PURE__*/_jsx(Section, {
      className: "p-top-4 p-bottom-6",
      style: Object.assign({}, commonStyles, {
        msGridRow: 4,
        gridRowStart: 4
      }),
      children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: !hasPricingAndPurchasingSucceeded,
        heights: [47],
        className: "m-bottom-7",
        children: /*#__PURE__*/_jsx(SuiteTileCTAsContainer, {
          productLevel: productLevel,
          trackingSource: SUITE_CARD,
          ctaStyles: {
            width: '100%',
            lineHeight: '23px'
          }
        })
      })
    }), /*#__PURE__*/_jsxs(Section, {
      style: Object.assign({}, commonStyles, {
        msGridRow: 5,
        gridRowStart: 5
      }),
      children: [/*#__PURE__*/_jsx("div", {
        style: {
          fontWeight: WEB_FONT_DEMI_BOLD_WEIGHT
        },
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "pricingPage.pages.crmSuite.includes"
        })
      }), /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: !hasPricingAndPurchasingSucceeded,
        Content: ProductDescriptions,
        contentProps: {
          productLevel
        }
      })]
    }), /*#__PURE__*/_jsxs(Section, {
      className: "text-center p-x-0",
      style: Object.assign({}, commonStyles, {
        msGridRow: 6,
        gridRowStart: 6
      }),
      children: [/*#__PURE__*/_jsx(StyledHR, {
        className: "m-y-4"
      }), /*#__PURE__*/_jsx("div", {
        children: /*#__PURE__*/_jsx(SuiteViewAllFeatures, {
          productLevel: productLevel,
          currencyCode: currencyCode
        })
      })]
    }), /*#__PURE__*/_jsxs(Section, {
      className: "p-bottom-4 text-center p-x-0",
      style: Object.assign({}, commonStyles, {
        msGridRow: 7,
        gridRowStart: 7
      }),
      children: [/*#__PURE__*/_jsx(StyledHR, {
        className: "m-y-4"
      }), /*#__PURE__*/_jsx("div", {
        children: /*#__PURE__*/_jsx(SuiteTileCalculatorAnchorLink, {
          productLevel: productLevel
        })
      })]
    })]
  });
};